import { useMemo } from 'react';
import { useLocalStorage, useUpdateEffect } from 'react-use';

import { useFormController, ValidationRules } from '@dreryk/react-common';
import { Checkbox, FormController } from '@dreryk/react-components';

import { translationKeys } from '../translations';

import { StyledPrintsForm } from './PrintsForm.styled';
import { Prints, PrintsFormBean, PrintsFormController, PrintsFormInternalBean } from './PrintsForm.types';

const DEFAULT_PRINTS: Prints = {
   summary: false,
   recommendations: false,
   otherDocuments: false,
   subjectiveAssessment: false,
};

type PrintsFormRenderProps = Pick<PrintsFormController, 'trigger'> & {
   prints: Prints;
};

type PrintsFormProps = {
   readonly children: (renderProps: PrintsFormRenderProps) => JSX.Element;
};

export const PrintsForm = (props: PrintsFormProps): JSX.Element => {
   const [storedVisitPrints, storeVisitPrints] = useLocalStorage<Prints>('visitPrints', DEFAULT_PRINTS);
   const { register, trigger, watch } = useFormController<PrintsFormBean & PrintsFormInternalBean>({
      ...(storedVisitPrints ?? DEFAULT_PRINTS),
      atLeastOne: false,
   });

   const prints = watch(['otherDocuments', 'subjectiveAssessment', 'recommendations', 'summary']);

   useUpdateEffect(() => {
      trigger('atLeastOne');
      storeVisitPrints({
         otherDocuments: prints.otherDocuments,
         subjectiveAssessment: prints.subjectiveAssessment,
         recommendations: prints.recommendations,
         summary: prints.summary,
      });
   }, [prints.summary, prints.recommendations, prints.otherDocuments, prints.subjectiveAssessment]);

   const validationRules: ValidationRules<PrintsFormBean> = useMemo(
      () => ({
         validate: (__, getValue) => {
            const currentSummary = getValue('summary');
            const currentRecommendations = getValue('recommendations');
            const currentOtherDocuments = getValue('otherDocuments');
            const currentSubjectiveAssessment = getValue('subjectiveAssessment');
            if (!currentSummary && !currentRecommendations && !currentOtherDocuments && !currentSubjectiveAssessment) {
               return translationKeys.chooseDocumentToPrint;
            }
            return undefined;
         },
      }),
      []
   );

   return (
      <StyledPrintsForm direction='column' gap='12px' as={'form'} noValidate>
         <Checkbox label={translationKeys.visitSummary} testId='summary' controller={register('summary')} />
         <Checkbox
            label={translationKeys.subjectiveAssessment}
            testId='subjectiveAssessment'
            controller={register('subjectiveAssessment')}
         />
         <Checkbox
            label={translationKeys.recommendations}
            testId='recommendations'
            controller={register('recommendations')}
         />
         <Checkbox
            label={translationKeys.otherDocuments}
            testId='otherDocuments'
            controller={register('otherDocuments')}
         />
         <FormController controller={register('atLeastOne', validationRules)} />
         {props.children({
            trigger: trigger,
            prints: prints,
         })}
      </StyledPrintsForm>
   );
};
